'use client';

import { useCallback, useState, useEffect } from 'react';
import { useYouTubeVideo } from '@/src/utils/youtube';
import { Icon } from '@/src/components/ui';
import { YouTubeModal } from '../../profile/components';
import { useProfileUIStore } from '../../profile/store';
import { cn } from '@workspace/ui/lib/utils';

interface YouTubePreviewProps {
  videoUrl: string;
}

export const YouTubePreview: React.FC<YouTubePreviewProps> = ({ videoUrl }) => {
  const isMobileView = useProfileUIStore((state) => state.isMobileView);
  const previewMode = useProfileUIStore((state) => state.previewMode);
  const { videoId, videoInfo, loading, error, updateUrl } =
    useYouTubeVideo(videoUrl);

  useEffect(() => {
    if (videoUrl) {
      updateUrl(videoUrl);
    }
  }, [videoUrl, updateUrl]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = useCallback(() => {
    if (videoId) setIsModalOpen(true);
  }, [videoId]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  if (loading) {
    return <div className="p-4 bg-gray-100 text-sm">Loading video…</div>;
  }
  if (error) {
    return <div className="p-4 bg-red-100 text-sm text-red-600">{error}</div>;
  }
  if (!videoInfo) {
    return null;
  }

  return (
    <>
      <div
        className={cn(
          'relative flex gap-3 p-1 bg-[#1E1E1E0D] rounded-lg shadow-sm cursor-pointer hover:bg-gray-100 transition',
          {
            'md:p-4': !isMobileView,
          }
        )}
        onClick={openModal}
      >
        <img
          src={videoInfo.thumbnail.medium}
          alt={videoInfo.title}
          className={cn('object-cover rounded w-30 h-24', {
            'w-[160px] h-[90px] rounded-[8px]': !isMobileView,
          })}
        />

        <div className="flex-1 text-start">
          <div className="flex gap-2 mb-1">
            <Icon name="youtube" className="size-5 text-red-600" />
            <p className={cn("font-medium text-sm", {
              'md:text-base': !isMobileView,
            })}>{videoInfo.title}</p>
          </div>

          {videoInfo.description && (
            <p className="text-sm text-gray-600 line-clamp-2">
              {videoInfo.description}
            </p>
          )}
        </div>
      </div>

      {videoId && (
        <YouTubeModal
          isOpen={isModalOpen}
          onClose={closeModal}
          videoId={videoId}
          title={videoInfo.title}
        />
      )}
    </>
  );
};
